import Color from 'color'
import { POSTER_OVERLAY_DEFAULT, POSTER_TEXT_COLOR_DEFAULT } from '@paintscout/util'

/**
 * Re-exported so blocks and their options panels import the poster defaults from one place.
 * They are defined in `@paintscout/util`'s constants because the block catalog needs them too, and
 * util cannot import from react-quote.
 */
export { POSTER_OVERLAY_DEFAULT, POSTER_TEXT_COLOR_DEFAULT }

export interface PosterStylesArgs {
  layout?: string
  /** Whether the block actually has an image behind the text. */
  hasImage: boolean
  color?: string
  overlay?: number
}

export interface PosterStyles {
  /** `''` means "inherit the presentation theme" — the caller must not force a colour. */
  textColor: string
  /** A ready `rgba()` string, or `null` when no scrim should render. */
  scrim: string | null
}

function clampOpacity(value: number): number {
  if (!Number.isFinite(value)) return POSTER_OVERLAY_DEFAULT
  return Math.min(1, Math.max(0, value))
}

/**
 * Whether text in `color` is light enough to need a dark scrim behind it.
 *
 * `Color()` throws on an unparseable string, and the colour picker's hex field can be read
 * mid-edit ('#ff'), so an unusable value has to fall back rather than break the render. Light is
 * the safer fallback: it produces a dark scrim, which is what the white default wants.
 */
function isLightText(color: string): boolean {
  try {
    return Color(color).isLight()
  } catch {
    return true
  }
}

/**
 * Resolves the text colour and scrim for a block that puts text directly over a full-bleed image.
 *
 * A text-shadow alone does not survive a busy image — the reported case was a scraped marketing
 * flyer with its own headline, logo and phone number baked into the pixels (#23083). So a block
 * with an image always gets a flat, fixed-opacity scrim across the *whole* image rather than a
 * bottom-edge fade: the requirement is that the text stays readable at any point behind it, not
 * just near one edge.
 *
 * The scrim follows the text colour rather than being hard-coded dark, because the same block
 * supports white and dark headlines.
 *
 * A block with no image gets neither a forced colour nor a scrim. Forcing white there would put
 * white text on the theme background, and the scrim would darken a panel with nothing behind it.
 */
export function getPosterStyles(args: PosterStylesArgs): PosterStyles {
  const { layout, hasImage, color, overlay } = args

  // `layout` is optional in the data and defaults to poster; anything else (collage) keeps the
  // author's colour untouched and never gets a scrim, since its text sits beside the image.
  const isPoster = !layout || layout === 'poster'

  if (!isPoster || !hasImage) {
    return { textColor: color ?? '', scrim: null }
  }

  const textColor = color || POSTER_TEXT_COLOR_DEFAULT
  const opacity = clampOpacity(overlay ?? POSTER_OVERLAY_DEFAULT)

  if (!opacity) {
    return { textColor, scrim: null }
  }

  const scrimRgb = isLightText(textColor) ? '0, 0, 0' : '255, 255, 255'

  return { textColor, scrim: `rgba(${scrimRgb}, ${opacity})` }
}
